<template>
  <div class="map-location-picker">
    <div ref="mapContainer" class="map-location-picker__map"></div>

    <div class="map-location-picker__meta mt-2">
      <div v-if="hasCoordinates" class="small">
        Coordonnées sélectionnées:
        <strong>{{ formattedLatitude }}, {{ formattedLongitude }}</strong>
      </div>
      <div v-else class="small text-muted">
        Cliquez sur la carte pour choisir un point GPS.
      </div>
      <div v-if="reverseGeocodeError" class="small text-warning mt-1">
        {{ reverseGeocodeError }}
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

export default {
  name: "MapLocationPicker",
  props: {
    value: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      map: null,
      marker: null,
      reverseGeocodeError: "",
      defaultCenter: {
        latitude: 45.1885,
        longitude: 5.7245
      },
      defaultZoom: 12
    };
  },
  computed: {
    hasCoordinates() {
      const gps = this.value;
      if (!gps || typeof gps !== "object") return false;
      return Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude);
    },
    formattedLatitude() {
      return this.hasCoordinates ? this.value.latitude.toFixed(6) : "";
    },
    formattedLongitude() {
      return this.hasCoordinates ? this.value.longitude.toFixed(6) : "";
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        this.syncMarkerWithCoordinates(newValue);
      }
    }
  },
  mounted() {
    this.initMap();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    buildShortAddress(payload) {
      if (!payload || typeof payload !== "object") return "";

      const address = payload.address && typeof payload.address === "object"
        ? payload.address
        : {};

      const streetParts = [];
      if (address.house_number) streetParts.push(address.house_number);
      if (address.road) streetParts.push(address.road);
      else if (address.pedestrian) streetParts.push(address.pedestrian);
      else if (address.footway) streetParts.push(address.footway);
      else if (address.residential) streetParts.push(address.residential);
      else if (address.path) streetParts.push(address.path);

      const street = streetParts.join(" ").trim();
      const city = address.city || address.town || address.village || address.municipality || address.hamlet || "";

      if (street && city) return `${street}, ${city}`;
      if (street) return street;
      if (city) return city;

      if (payload.display_name && typeof payload.display_name === "string") {
        return payload.display_name.split(",").slice(0, 2).join(",").trim();
      }

      return "";
    },
    initMap() {
      const center = this.hasCoordinates
        ? [this.value.latitude, this.value.longitude]
        : [this.defaultCenter.latitude, this.defaultCenter.longitude];

      this.map = L.map(this.$refs.mapContainer).setView(center, this.defaultZoom);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap"
      }).addTo(this.map);

      this.map.on("click", this.handleMapClick);
      this.syncMarkerWithCoordinates(this.value);
      this.$nextTick(() => this.invalidateSize());
    },
    invalidateSize() {
      if (!this.map) return;
      this.map.invalidateSize();
    },
    handleMapClick(event) {
      if (this.disabled) return;
      const latitude = event.latlng.lat;
      const longitude = event.latlng.lng;
      this.updateCoordinates(latitude, longitude);
      this.reverseGeocode(latitude, longitude);
    },
    syncMarkerWithCoordinates(gps) {
      if (!this.map) return;
      if (!gps || !Number.isFinite(gps.latitude) || !Number.isFinite(gps.longitude)) {
        return;
      }

      const position = [gps.latitude, gps.longitude];
      if (!this.marker) {
        this.marker = L.marker(position).addTo(this.map);
      } else {
        this.marker.setLatLng(position);
      }
    },
    updateCoordinates(latitude, longitude) {
      const normalized = {
        latitude: Number(latitude),
        longitude: Number(longitude)
      };

      this.$emit("input", normalized);
      this.syncMarkerWithCoordinates(normalized);
    },
    async reverseGeocode(latitude, longitude) {
      this.reverseGeocodeError = "";
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`;

      try {
        const response = await fetch(url, {
          headers: {
            Accept: "application/json"
          }
        });
        if (!response.ok) {
          throw new Error("Reverse geocoding error");
        }
        const payload = await response.json();
        if (payload && payload.display_name) {
          const shortAddress = this.buildShortAddress(payload);
          this.$emit("resolved-address", shortAddress || payload.display_name);
        }
      } catch (error) {
        this.reverseGeocodeError = "Adresse introuvable automatiquement. Vous pouvez la saisir manuellement.";
      }
    }
  }
};
</script>

<style scoped>
.map-location-picker__map {
  width: 100%;
  height: 280px;
  border: 1px solid #dfe4ea;
  border-radius: 0.5rem;
}
</style>
